import { useMemo } from "react";

import { TaskSource } from "@fartherfinance/frontend/api/Tasks/Types";
import useGetTaskResources from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskResources";
import { Custodian } from "@fartherfinance/frontend/api/Types";

import { mapTaskSourceToLabel } from "../../Tasks/common";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { EnhancedAutocompleteOptionV2 } from "@src/multiCustodian/pages/Advisor/common";

export interface TaskResourceOptions {
  advisorsOptions: EnhancedAutocompleteOptionV2<string>[];
  assigneesOptions: EnhancedAutocompleteOptionV2<string>[];
  clientsOptions: EnhancedAutocompleteOptionV2<string>[];
  serviceTeamsOptions: EnhancedAutocompleteOptionV2<string>[];
  taskTypesOptions: EnhancedAutocompleteOptionV2<string>[];
  taskStatusesOptions: EnhancedAutocompleteOptionV2<string>[];
  taskPrioritiesOptions: EnhancedAutocompleteOptionV2<string>[];
  custodianOptions: EnhancedAutocompleteOptionV2<Custodian>[];
  generationOptions: EnhancedAutocompleteOptionV2<TaskSource>[];
}

// TODO: sort options
const useTaskResourceOptions = (): TaskResourceOptions => {
  const auth = useAdvisorRequestAuth();
  const taskResourcesQuery = useGetTaskResources(auth);

  const taskResources = taskResourcesQuery.data;
  const {
    advisorsMap,
    clientsMap,
    serviceTeamsMap,
    taskTypesMap,
    taskStatusesMap,
    taskPrioritiesMap,
  } = taskResources ?? {};

  const advisorsOptions = useMemo(() => {
    if (!advisorsMap) {
      return [];
    }

    return Array.from(advisorsMap.entries()).map(([id, advisor]) => ({
      label: `${advisor.name.first} ${advisor.name.last}`,
      value: id,
    }));
  }, [advisorsMap]);

  const clientsOptions = useMemo(() => {
    if (!clientsMap) {
      return [];
    }

    return Array.from(clientsMap.entries()).map(([id, client]) => ({
      label: `${client.name.first} ${client.name.last}`,
      value: id,
    }));
  }, [clientsMap]);

  const serviceTeamsOptions = useMemo(() => {
    if (!serviceTeamsMap) {
      return [];
    }

    return Array.from(serviceTeamsMap.entries()).map(([id, serviceTeam]) => ({
      label: serviceTeam.name,
      value: id,
    }));
  }, [serviceTeamsMap]);

  const taskTypesOptions = useMemo(() => {
    if (!taskTypesMap) {
      return [];
    }

    return Array.from(taskTypesMap.entries()).map(([id, taskType]) => ({
      label: taskType.displayName || taskType.name,
      value: id,
    }));
  }, [taskTypesMap]);

  const taskStatusesOptions = useMemo(() => {
    if (!taskStatusesMap) {
      return [];
    }

    return Array.from(taskStatusesMap.entries()).map(([id, status]) => ({
      label: status.displayValue ?? "",
      value: id,
    }));
  }, [taskStatusesMap]);

  const taskPrioritiesOptions = useMemo(() => {
    if (!taskPrioritiesMap) {
      return [];
    }

    return Array.from(taskPrioritiesMap.entries()).map(([id, priority]) => ({
      label: priority.displayValue ?? "",
      value: id,
    }));
  }, [taskPrioritiesMap]);

  const custodianOptions = useMemo(
    () =>
      Custodian.options.map((custodian) => ({
        label: custodian,
        value: custodian,
      })),
    []
  );

  const generationOptions = useMemo(
    () =>
      TaskSource.options.map((source) => ({
        label: mapTaskSourceToLabel(source),
        value: source,
      })),
    []
  );

  return {
    advisorsOptions,
    assigneesOptions: advisorsOptions,
    clientsOptions,
    serviceTeamsOptions,
    taskTypesOptions,
    taskStatusesOptions,
    taskPrioritiesOptions,
    custodianOptions,
    generationOptions,
  };
};

export default useTaskResourceOptions;
